/**
 * Session-locked feature flags
 *
 * This module provides a mechanism to lock certain feature flags for the duration
 * of a page session. Once a flag is locked, its value won't change even if
 * Split.io updates it, preventing inconsistencies in the application.
 *
 * The locked values are stored in memory and automatically cleared when the page
 * is refreshed (since JavaScript runtime is restarted).
 */

export const SESSION_LOCKED_FLAGS = [
  'build_configure_api_modal',
  // Swaps the Execution and Flow panel layouts, so it must not change mid-session.
  'build_executions_test_cases_ux_review'
] as const;

type LockedFlagsMap = Record<string, string>;

const lockedFlagsCache: LockedFlagsMap = {};

export const isSessionLockedFlag = (flagName: string): boolean =>
  (SESSION_LOCKED_FLAGS as readonly string[]).includes(flagName);

export const getLockedFlagValue = (flagName: string): string | null => {
  if (!isSessionLockedFlag(flagName)) {
    return null;
  }

  return lockedFlagsCache[flagName] || null;
};

export const lockFlagValue = (flagName: string, value: string): void => {
  if (!isSessionLockedFlag(flagName)) {
    return;
  }

  lockedFlagsCache[flagName] = value;
};
